import { RiskNotice } from "../RiskNotice";
import React, { useState } from "react";
import { customerApi, apiError } from "@/lib/customerApi";
import ChangePlan from "./ChangePlan";
import LaunchNotify, { useSalesOpen } from "../LaunchNotify";
import { pixel } from "@/lib/metaPixel";
import { DiscountBanner, discounted, useMyDiscount } from "./DiscountBanner";

const PLAN_NAMES = { single: "Single bot", five: "Five bots", all: "Everything" };
const PLAN_PRICES = { single: 49, five: 149, all: 249 };

const TIERS = [
  { id: "single", name: "Single bot", price: 49, line: "Software licence for any 1 listed bot." },
  { id: "five", name: "Five bots", price: 149, line: "Software licences for any 5 listed bots." },
  { id: "all", name: "Everything", price: 249, line: "Software licences for every listed bot." },
];

export default function PlanSection({ me, preselect }) {
  const salesOpen = useSalesOpen();
  const [tier, setTier] = useState(preselect || "five");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const s = me.subscription || {};
  const disc = useMyDiscount();

  const go = async (path, body) => {
    setBusy(true); setErr("");
    try {
      const { data } = await customerApi.post(path, body);
      if (data.checkout_url && body?.tier) pixel.initiateCheckout(PLAN_NAMES[body.tier], PLAN_PRICES[body.tier]);
      window.location.href = data.checkout_url || data.url;
    } catch (e) {
      setErr(apiError(e));
      setBusy(false);
    }
  };

  if (s.active) {
    const end = s.current_period_end ? new Date(s.current_period_end * 1000).toLocaleDateString("en-GB") : "-";
    return (
      <section data-testid="plan-active" className="border border-[#2ECA7F]/40 bg-[#2ECA7F]/5 rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#2ECA7F]">Your plan · {s.status}</div>
          <div className="text-2xl font-medium mt-1" data-testid="plan-name">{s.tier_name}</div>
          <div className="text-xs text-[#94A3B8] mt-1" data-testid="plan-renewal">
            {s.cancel_at_period_end ? `Cancelled - bots stop working on ${end}` : `Renews ${end}`}
          </div>
        </div>
        <button onClick={() => go("/store/account/portal", { origin_url: window.location.origin })} disabled={busy} data-testid="plan-manage-billing"
          className="mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg border border-[#2ECA7F] text-[#2ECA7F] hover:bg-[#2ECA7F]/10 transition-colors disabled:opacity-50">
          Manage billing / cancel
        </button>
        {err && <div className="w-full text-xs text-[#F87171]" data-testid="plan-error">{err}</div>}
        {!s.cancel_at_period_end && <ChangePlan current={s.tier} />}
      </section>
    );
  }
  if (salesOpen === false && !me.unlimited_swaps) return <><LaunchNotify defaultEmail={me.email} testid="plan-launch-notify" /><DiscountBanner d={disc} /><RiskNotice testid="plan-risk" className="mt-4" /></>;
  return (
    <section data-testid="plan-choose" className="border border-blue-900/40 bg-[#020617]/70 rounded-xl p-6">
      <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24]">Step 1 · choose a plan</div>
      <DiscountBanner d={disc} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        {TIERS.map((t) => (
          <button key={t.id} onClick={() => setTier(t.id)} data-testid={`plan-tier-${t.id}`}
            className={`text-left rounded-xl border p-5 transition-colors ${tier === t.id ? "border-[#FBBF24] bg-[#FBBF24]/10" : "border-blue-900/50 hover:border-[#38BDF8]"}`}>
            <div className="mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{t.name}</div>
            <div className="text-3xl font-medium mt-2">
              {disc ? <><span className="text-lg text-[#64748B] line-through mr-2">£{t.price}</span>£{discounted(t.price, disc)}</> : <>£{t.price}</>}
              <span className="text-sm text-[#94A3B8]">/mo</span>
            </div>
            {disc && <div className="mono text-[10px] text-[#FBBF24] mt-1">first month, then £{t.price}/mo</div>}
            <div className="text-xs text-[#CBD5E1] mt-2">{t.line}</div>
          </button>
        ))}
      </div>
      <label className="flex items-start gap-2 mt-5 text-xs text-[#CBD5E1] cursor-pointer">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="plan-terms-checkbox" className="mt-0.5 accent-[#FBBF24]" />
        I understand I'm buying a software licence, not investment advice or account management. I choose, install and run the software myself on my own MT5 account. Past and backtested results aren't reliable indicators of future performance and I can lose money. The plan renews monthly until I cancel.
      </label>
      {err && <div className="text-xs text-[#F87171] mt-3" data-testid="plan-error">{err}</div>}
      <button disabled={!terms || busy} onClick={() => go("/store/account/checkout", { tier, origin_url: window.location.origin, accepted_terms: terms })}
        data-testid="plan-checkout-btn" className="mt-5 mono text-[11px] uppercase tracking-widest px-6 py-3 rounded-lg bg-[#FBBF24] text-[#050914] disabled:opacity-40 hover:scale-[1.02] transition-transform">
        {busy ? "Opening secure checkout…" : "Continue to secure checkout"}
      </button>
      <div className="text-[10px] text-[#64748B] mt-2 mono">Secure payments by Stripe</div>
      <RiskNotice testid="plan-risk" className="mt-5" />
    </section>
  );
}
